import React from 'react'
import { useIsMobile } from '../CommunityProfileCarousels/index'
import CommunityPlaylistRowHeaderBase from './CommunityPlaylistRowHeaderBase'

interface CommunityPlaylistRowHeaderProps {
  name: string
  badge?: { label: string; variant: 'progress' | 'topic' } | null
  episodeCount?: number
  totalDuration?: string | null
  authorDisplay?: string | null
  authorUid?: string | null
  followerCount?: number
  nameAction?: React.ReactNode
  editMode?: boolean
  editingName?: string
  onNameChange?: ((value: string) => void) | null
  statusTag?: React.ReactNode
  shareButton?: React.ReactNode
  /** Optional replacement for the default stats block (e.g. Currently
   * Watching renders series count + overall progress). Forwarded to Base. */
  customStats?: React.ReactNode
  /** Pending-hydration flag — when true, Base renders invisible placeholder
   * `__stat` spans so the row's height stays stable across the hydration
   * window. Ignored when `customStats` is set. */
  statsPending?: boolean
}

/**
 * Community-specific wrapper around the shared `playlist-row-header` markup.
 *
 * Layout:
 * - The wrapper is a thin container; it does not split into columns.
 * - `nameAction` and `shareButton` always live inside the `__actions` cluster
 *   that renders inside `playlist-row-header__name-row` at the right edge.
 *   The actions cluster never wraps below the title — the title shrinks if
 *   needed (`flex: 0 1 auto`, `min-width: 0`).
 * - `statusTag` placement is viewport-conditional via `useIsMobile()`
 *   (which matches `max-width: 767px`):
 *     - Desktop / tablet (>=768px): rendered inside the `__actions` cluster
 *       immediately to the left of the share button.
 *     - Mobile (<=767px):           rendered as its own row beneath the
 *       name row, left-aligned (`__status-tag-row`).
 *   Only one React instance of the status tag is rendered at any time, so
 *   internal state (open dropdown, pending follow mutation) is preserved.
 */
function CommunityPlaylistRowHeader ({
  name,
  badge = null,
  episodeCount = 0,
  totalDuration = null,
  authorDisplay = null,
  authorUid = null,
  followerCount = 0,
  nameAction,
  editMode = false,
  editingName,
  onNameChange = null,
  statusTag,
  shareButton,
  customStats,
  statsPending = false,
}: CommunityPlaylistRowHeaderProps) {
  const isMobile = useIsMobile()

  const hasStructuredSlots = statusTag != null || shareButton != null

  // Place statusTag inline with actions on desktop/tablet; below the title
  // on mobile.
  const statusTagInActions = !isMobile ? statusTag : null
  const statusTagBelowTitle = isMobile ? statusTag : null

  // Right-aligned actions cluster.
  // Order on desktop: [nameAction, statusTag, shareButton] — statusTag sits
  // immediately to the left of the share button.
  const hasAnyAction = nameAction != null || shareButton != null || statusTagInActions != null
  const actionsCluster = hasAnyAction ? (
    <>
      {nameAction ? (
        <span className="community-playlist-row-header__name-action">{nameAction}</span>
      ) : null}
      {statusTagInActions}
      {shareButton}
    </>
  ) : null

  // Base receives the cluster only when structured slots are set; a lone
  // `nameAction` (no statusTag / shareButton) uses the legacy single-pill
  // slot on the name row.
  const baseActions = hasStructuredSlots && hasAnyAction ? actionsCluster : null

  const wrapperClassName = [
    'community-playlist-row-header',
    editMode ? 'community-playlist-row-header--editing' : null,
  ].filter(Boolean).join(' ')

  return (
    <div className={wrapperClassName}>
      <CommunityPlaylistRowHeaderBase
        name={name}
        badge={badge}
        episodeCount={episodeCount}
        totalDuration={totalDuration}
        authorDisplay={authorDisplay}
        authorUid={authorUid}
        followerCount={followerCount}
        // Legacy single-pill nameAction only when there is no structured
        // cluster (otherwise nameAction is folded into `actions`).
        nameAction={hasStructuredSlots ? undefined : nameAction}
        actions={baseActions}
        statusTagRow={statusTagBelowTitle}
        customStats={customStats}
        statsPending={statsPending}
        editMode={editMode}
        editingName={editingName}
        onNameChange={onNameChange}
      />
    </div>
  )
}

export default React.memo(CommunityPlaylistRowHeader)
